/**
 * Segmented control — a row of joined toggle buttons, one active (the
 * menu-history chart range picker, confirm-dialog per-row choices).
 *
 *   const seg = createSegmented({
 *     ariaLabel: 'Chart range',
 *     options: [{ value: '3m', label: '3 months', short: '3 mo' }, …],
 *     value: '3m',
 *     onChange: (value) => …,
 *   });
 *   host.append(seg.el); seg.setValue('6m');
 *
 * It adapts to the width it gets, in order of preference:
 *   1. one row (on narrow screens the options stretch to share the width);
 *   2. one row with each option's `short` label (when any option has one);
 *   3. wrapped into a tidy grid (`.is-wrapped`: rounded rectangle, the
 *      dividers drawn per option so every row/column line stays clean).
 * Styles live in components.css (`.segmented`).
 */

/**
 * Pick the layout for a segmented element given a `wraps()` probe that reports
 * whether its options currently span more than one line. Pure apart from the
 * classes/labels it sets, so it's unit-testable with a fake probe.
 * @returns {'row'|'compact'|'wrapped'}
 */
export function fitSegmented(el, wraps = () => optionsWrap(el)) {
  const options = [...el.querySelectorAll('.segmented-option')];
  const setCompact = (on) => {
    for (const b of options) {
      if (!b.dataset.short) continue;
      b.textContent = on ? b.dataset.short : b.dataset.label;
      if (on) b.setAttribute('aria-label', b.dataset.label);
      else b.removeAttribute('aria-label');
    }
  };
  el.classList.remove('is-compact', 'is-wrapped');
  setCompact(false);
  if (!wraps()) return 'row';
  if (options.some((b) => b.dataset.short)) {
    el.classList.add('is-compact');
    setCompact(true);
    if (!wraps()) return 'compact';
  }
  el.classList.add('is-wrapped');
  return 'wrapped';
}

/** True when the options' boxes start on more than one line. */
function optionsWrap(el) {
  const options = el.querySelectorAll('.segmented-option');
  if (options.length < 2) return false;
  return options[options.length - 1].offsetTop > options[0].offsetTop;
}

/**
 * `tabs: true` makes it a TAB STRIP (the shares page's "All shares" views):
 * role tablist/tab, `aria-selected` instead of `aria-pressed`, a roving
 * tabindex and arrow / Home / End keys that move + select; `controls` (an id)
 * becomes each tab's `aria-controls` (one shared panel the caller re-fills).
 * @param {{ options: Array<{ value: any, label: string, short?: string, danger?: boolean }>,
 *   value?: any, ariaLabel?: string, className?: string, tabs?: boolean,
 *   controls?: string, onChange?: (value: any) => void }} opts
 * @returns {{ el: HTMLElement, setValue: (value: any) => void, getValue: () => any }}
 */
export function createSegmented({
  options,
  value,
  ariaLabel,
  className,
  tabs = false,
  controls,
  onChange,
} = {}) {
  const el = document.createElement('div');
  el.className = className ? `segmented ${className}` : 'segmented';
  el.setAttribute('role', tabs ? 'tablist' : 'group');
  if (ariaLabel) el.setAttribute('aria-label', ariaLabel);

  let current = value ?? options[0]?.value;
  const select = (v) => {
    if (v === current) return;
    setValue(v);
    onChange?.(v);
  };
  const buttons = options.map((opt) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'segmented-option';
    if (opt.danger) b.classList.add('segmented-danger');
    b.textContent = opt.label;
    if (opt.short && opt.short !== opt.label) {
      b.dataset.label = opt.label;
      b.dataset.short = opt.short;
    }
    if (tabs) {
      b.setAttribute('role', 'tab');
      if (controls) b.setAttribute('aria-controls', controls);
    }
    b.addEventListener('click', () => select(opt.value));
    el.appendChild(b);
    return { b, value: opt.value };
  });

  if (tabs) {
    const KEYS = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    el.addEventListener('keydown', (e) => {
      const i = buttons.findIndex(({ value: v }) => v === current);
      let next = null;
      if (e.key in KEYS) next = (i + KEYS[e.key] + buttons.length) % buttons.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = buttons.length - 1;
      if (next === null) return;
      e.preventDefault();
      select(buttons[next].value);
      buttons[next].b.focus();
    });
  }

  function setValue(next) {
    current = next;
    for (const { b, value: v } of buttons) {
      const active = v === next;
      b.classList.toggle('is-active', active);
      if (tabs) {
        b.setAttribute('aria-selected', String(active));
        b.tabIndex = active ? 0 : -1;
      } else {
        b.setAttribute('aria-pressed', String(active));
      }
    }
  }
  setValue(current);

  sharedObserver()?.observe(el);

  return { el, setValue, getValue: () => current };
}

/**
 * One ResizeObserver for every segmented control: re-fit on any width change
 * (each fit settles deterministically for a width, so it quiesces). Controls
 * not in the document are skipped (they can't be measured).
 */
let observer = null;
function sharedObserver() {
  if (typeof ResizeObserver !== 'function') return null;
  observer ??= new ResizeObserver((entries) => {
    for (const { target } of entries) if (target.isConnected) fitSegmented(target);
  });
  return observer;
}
